import React, { useRef, useState } from "react";
import { api } from "../../services/api";
import { useNavigate } from "react-router-dom";
import SiteNav from "../../components/SiteNav";
import TeamNameStep from "./steps/TeamNameStep";
import KitCreationStep from "./steps/KitCreationStep";
import GuestCheckoutStep from "./steps/GuestCheckoutStep";
import { BackPrintConfig } from "../../types/props";
import "./buildkit.css";

const TOTAL_STEPS = 3;

const BuildKit: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Team phase state
  const [prompt, setPrompt] = useState("");
  const [teamName, setTeamName] = useState("");
  const [logoOptions, setLogoOptions] = useState<string[]>([]);
  const [selectedLogo, setSelectedLogo] = useState("");

  // Kit phase state
  const [kitType, setKitType] = useState("tshirt");
  const [teamwearColor, setTeamwearColor] = useState("#1E3A8A");
  const [emblemColor, setEmblemColor] = useState("#FFFFFF");
  const [manualEmblemColor, setManualEmblemColor] = useState(false);
  const [backPrint, setBackPrint] = useState<BackPrintConfig>({
    enabled: false, text: "", font: "Arial", scale: 50, color: "#FFFFFF", position: 50,
  });
  const [showBackImage, setShowBackImage] = useState(true);
  const [backImageScale, setBackImageScale] = useState(150);
  const [designName, setDesignName] = useState("");

  const [logosLoading, setLogosLoading] = useState(false);
  // Name the current logos were generated for — logos embed the name, so a new name needs new logos
  const [logosForName, setLogosForName] = useState("");
  const logoRequestId = useRef(0);

  // Captured preview data URLs
  const [previewFrontDataUrl, setPreviewFrontDataUrl] = useState("");
  const [previewBackDataUrl, setPreviewBackDataUrl] = useState("");

  const displayTeamName = teamName.trim() || "Team Name";

  const startLogoGeneration = (name: string, description: string) => {
    const trimmedName = name.trim();
    if (trimmedName === logosForName && (logosLoading || logoOptions.length > 0)) return;
    const context = description.trim() || trimmedName;
    const requestId = ++logoRequestId.current;
    setLogosForName(trimmedName);
    setLogoOptions([]);
    setSelectedLogo("");
    setLogosLoading(true);
    api.generateLogo(context, trimmedName, 2)
      .then((data: { logo_images?: string[] }) => {
        if (requestId !== logoRequestId.current) return;
        setLogoOptions(data.logo_images || []);
        if (data.logo_images?.length) setSelectedLogo(data.logo_images[0]);
      })
      .catch(() => { /* silent — KitCreationStep will show its own error UI and retry */ })
      .finally(() => { if (requestId === logoRequestId.current) setLogosLoading(false); });
  };

  const handlePreviewReady = (name: string, front: string, back: string) => {
    setDesignName(name);
    setPreviewFrontDataUrl(front);
    setPreviewBackDataUrl(back);
    setStep(3);
  };

  // Step 1: team define phase (narrow)
  // Step 2: kit creation phase (wide)
  // Step 3: checkout
  const contentClass =
    step === 1 ? "bk-step-wrap" :
    step === 2 ? "bk-kit-wrap" :
    "bk-content";

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <TeamNameStep
            step={step}
            totalSteps={TOTAL_STEPS}
            prompt={prompt}
            setPrompt={setPrompt}
            teamName={teamName}
            setTeamName={setTeamName}
            onStartLogoGeneration={startLogoGeneration}
            onNext={() => setStep(2)}
            onBack={() => navigate("/")}
          />
        );
      case 2:
        return (
          <KitCreationStep
            step={step}
            totalSteps={TOTAL_STEPS}
            prompt={prompt}
            teamName={displayTeamName}
            logoOptions={logoOptions}
            setLogoOptions={setLogoOptions}
            selectedLogo={selectedLogo}
            setSelectedLogo={setSelectedLogo}
            logosLoading={logosLoading}
            kitType={kitType}
            setKitType={setKitType}
            teamwearColor={teamwearColor} setTeamwearColor={setTeamwearColor}
            emblemColor={emblemColor} setEmblemColor={setEmblemColor}
            manualEmblemColor={manualEmblemColor} setManualEmblemColor={setManualEmblemColor}
            showBackImage={showBackImage} setShowBackImage={setShowBackImage}
            backImageScale={backImageScale} setBackImageScale={setBackImageScale}
            designName={designName} setDesignName={setDesignName}
            onPreviewReady={handlePreviewReady}
            onBack={() => setStep(1)}
          />
        );
      case 3:
        return (
          <GuestCheckoutStep
            teamName={displayTeamName}
            teamSummary=""
            teamLogoDataUrl={selectedLogo}
            kitType={kitType}
            teamwearColor={teamwearColor}
            emblemColor={emblemColor}
            manualEmblemColor={manualEmblemColor}
            teamLogo={selectedLogo}
            backPrint={backPrint}
            showBackImage={showBackImage}
            backImageScale={backImageScale}
            designName={designName}
            previewFrontDataUrl={previewFrontDataUrl}
            previewBackDataUrl={previewBackDataUrl}
            onBack={() => setStep(2)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bk-page">
      <div className="grain-overlay" />
      <SiteNav />
      <div className={contentClass}>
        {renderStep()}
      </div>
    </div>
  );
};

export default BuildKit;
